// Organisation-wide or one venue (owner 2026-09-30, #709; spec/06 §3.1, §8.5): the one choice a pass product and a
// coupon batch make about where they can be used. Two plain options — every venue in the organisation, or one venue
// picked from the organisation's — and, for a pass made before the choice existed with a scope it cannot express (the
// network, or several venues), a third that keeps it as it is. The server refuses a venue-scoped pass or coupon at
// every other venue; the kiosk and the desk show only what is usable where they are.
import { useTranslation } from '@clavian/i18n/react';
import { RadioGroup, SelectField } from '@clavian/ui-web';
import type { ReactNode } from 'react';

import './pricing.css';

/** `organization`, `venue` (with `venueId`), or `keep` — an older scope left untouched. */
export type ScopeChoiceKind = 'organization' | 'venue' | 'keep';

export interface ScopeChoiceProps {
  readonly label: string;
  readonly kind: ScopeChoiceKind;
  readonly venueId: string;
  readonly venues: readonly { readonly id: string; readonly name: string }[];
  readonly onKindChange: (kind: ScopeChoiceKind) => void;
  readonly onVenueChange: (venueId: string) => void;
  /** Words for the older scope `keep` stands for; absent, `keep` is not offered. */
  readonly keepLabel?: string;
  readonly error?: string;
}

export function ScopeChoice({ label, kind, venueId, venues, onKindChange, onVenueChange, keepLabel, error }: ScopeChoiceProps): ReactNode {
  const { t } = useTranslation('console');
  const option = (title: string, hint: string): ReactNode => (
    <span className="console-scope-choice__option">
      <span>{title}</span>
      <span className="console-subtle">{hint}</span>
    </span>
  );
  return (
    <div className="console-scope-choice">
      <RadioGroup
        label={label}
        value={kind}
        onValueChange={(value) => onKindChange(value as ScopeChoiceKind)}
        options={[
          { value: 'organization', label: option(t('pricing.scopeChoice.organization'), t('pricing.scopeChoice.organizationHint')) },
          { value: 'venue', label: option(t('pricing.scopeChoice.venue'), t('pricing.scopeChoice.venueHint')) },
          ...(keepLabel === undefined ? [] : [{ value: 'keep', label: t('pricing.scopeChoice.keep', { what: keepLabel }) }]),
        ]}
      />
      {kind === 'venue' ? (
        <SelectField
          label={t('pricing.scopeChoice.pickVenue')}
          required
          value={venueId}
          onValueChange={onVenueChange}
          options={venues.map((venue) => ({ value: venue.id, label: venue.name }))}
          error={error}
        />
      ) : null}
    </div>
  );
}
